<template>
  <div
    ref="container"
    class="flex flex-col gap-0.5 overflow-y-auto p-3"
    :class="[$style.root]"
    @keydown="handleKeydown"
  >
    <div
      v-for="year in availableYears"
      ref="years"
      :key="`${year}:${year === cursor.year}`"
      :data-year="year"
      class="flex flex-col gap-0.5"
    >
      <button
        type="button"
        :tabindex="-1"
        :disabled="year === cursor.year"
        :class="[
          'rounded px-2 py-1 text-left text-sm transition-colors',
          year === cursor.year
            ? 'font-medium'
            : 'text-muted-foreground hover:bg-card hover:text-foreground',
        ]"
        @click="selectYear(year)"
      >
        {{ year }}
      </button>

      <div
        v-if="year === cursor.year"
        role="grid"
        aria-label="Month"
        class="mb-1 grid grid-cols-4 gap-1"
      >
        <button
          v-for="month in availableMonths"
          :key="month.value"
          ref="months"
          type="button"
          :data-month="month.value"
          :disabled="month.disabled"
          :tabindex="month.value === cursor.month ? 0 : -1"
          :aria-selected="month.value === cursor.month"
          :class="[
            'rounded px-2 py-1.5 text-sm  transition-colors hover:bg-card',
            'outline-2 outline-blue-500 focus-visible:outline',
            'disabled:pointer-events-none disabled:opacity-30',
          ]"
          @click="selectMonth(month.value)"
          @focus="cursor = createCursor(cursor.year, month.value)"
        >
          {{ month.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, shallowRef, useTemplateRef, watch } from "vue";

import { formatMonthShort } from "@/lib/time";

const props = defineProps<{
  initialCursor: Date;
  min: Date | null;
  max: Date | null;
}>();

const emit = defineEmits<{
  jump: [date: Date];
}>();

const currentYear = new Date().getFullYear();
const minYear = computed(() => props.min?.getFullYear() ?? currentYear - 100);
const maxYear = computed(() => props.max?.getFullYear() ?? currentYear + 20);

const availableYears = computed(() => {
  const result: number[] = [];
  for (let y = minYear.value; y <= maxYear.value; y++) {
    result.push(y);
  }
  return result;
});

const isMonthDisabled = (month: number, year: number): boolean => {
  const min = props.min;
  const max = props.max;

  if (min) {
    const minYr = min.getFullYear();
    const minMo = min.getMonth() + 1;
    if (year < minYr || (year === minYr && month < minMo)) {
      return true;
    }
  }

  if (max) {
    const maxYr = max.getFullYear();
    const maxMo = max.getMonth() + 1;
    if (year > maxYr || (year === maxYr && month > maxMo)) {
      return true;
    }
  }

  return false;
};

const clampMonth = (month: number, year: number): number => {
  const min = props.min;
  const max = props.max;

  const rangeStart = min && year === min.getFullYear() ? min.getMonth() + 1 : 1;
  const rangeEnd = max && year === max.getFullYear() ? max.getMonth() + 1 : 12;

  // Year completely out of bounds — no valid months
  if ((min && year < min.getFullYear()) || (max && year > max.getFullYear())) {
    return month;
  }

  if (month >= rangeStart && month <= rangeEnd) {
    return month;
  }
  return rangeStart;
};

// consistent object shape that JS engines like
const createCursor = (year: number, month: number) => {
  return { year, month };
};

const cursor = shallowRef(
  createCursor(
    props.initialCursor.getFullYear(),
    clampMonth(
      props.initialCursor.getMonth() + 1,
      props.initialCursor.getFullYear()
    )
  )
);

interface MonthItem {
  value: number;
  label: string;
  disabled: boolean;
}

const availableMonths = computed<MonthItem[]>(() => {
  const year = cursor.value.year;
  const result: MonthItem[] = [];
  for (let m = 1; m <= 12; m++) {
    result.push({
      value: m,
      label: formatMonthShort(m),
      disabled: isMonthDisabled(m, year),
    });
  }

  return result;
});

const container = useTemplateRef("container");
const years = useTemplateRef("years");
const months = useTemplateRef("months");

const selectYear = (year: number): void => {
  cursor.value = createCursor(year, clampMonth(cursor.value.month, year));

  nextTick(() => {
    scrollSelectedYearIntoView();
    focusMonth(cursor.value.month);
  });
};

const selectMonth = (month: number): void => {
  cursor.value = createCursor(cursor.value.year, month);
  emit("jump", new Date(cursor.value.year, month - 1, 1));
};

const scrollSelectedYearIntoView = (): void => {
  const scroller = container.value;
  if (!scroller) {
    return;
  }

  const syear = "" + cursor.value.year;

  const item = years.value?.find((el) => el.dataset.year === syear);
  if (!item) {
    return;
  }

  const centerOffset = (scroller.clientHeight - item.offsetHeight) / 2;
  scroller.scrollTop = Math.max(0, item.offsetTop - centerOffset);
};

const focusMonth = (month: number): void => {
  const smonth = "" + month;

  const btn = months.value?.find((b) => b.dataset.month === smonth);
  btn?.focus({ preventScroll: true });
};

const navigateTo = (year: number, month: number): void => {
  const yearIdx = availableYears.value.indexOf(year);
  if (yearIdx === -1) {
    return;
  }

  cursor.value = createCursor(year, month);

  nextTick(() => {
    scrollSelectedYearIntoView();
    focusMonth(month);
  });
};

const handleKeydown = (event: KeyboardEvent): void => {
  const { year, month } = cursor.value;
  const yearIdx = availableYears.value.indexOf(year);

  switch (event.key) {
    case "ArrowLeft": {
      event.preventDefault();
      if (month > 1) {
        navigateTo(year, month - 1);
      } else if (yearIdx > 0) {
        navigateTo(availableYears.value[yearIdx - 1], 12);
      }
      break;
    }
    case "ArrowRight": {
      event.preventDefault();
      if (month < 12) {
        navigateTo(year, month + 1);
      } else if (yearIdx < availableYears.value.length - 1) {
        navigateTo(availableYears.value[yearIdx + 1], 1);
      }
      break;
    }
    case "ArrowUp": {
      event.preventDefault();
      if (month > 4) {
        navigateTo(year, month - 4);
      } else if (yearIdx > 0) {
        navigateTo(availableYears.value[yearIdx - 1], month + 8);
      }
      break;
    }
    case "ArrowDown": {
      event.preventDefault();
      if (month <= 8) {
        navigateTo(year, month + 4);
      } else if (yearIdx < availableYears.value.length - 1) {
        navigateTo(availableYears.value[yearIdx + 1], month - 8);
      }
      break;
    }
    case "PageUp": {
      event.preventDefault();
      if (yearIdx > 0) {
        navigateTo(availableYears.value[yearIdx - 1], month);
      }
      break;
    }
    case "PageDown": {
      event.preventDefault();
      if (yearIdx < availableYears.value.length - 1) {
        navigateTo(availableYears.value[yearIdx + 1], month);
      }
      break;
    }
    case "Home": {
      event.preventDefault();
      navigateTo(year, 1);
      break;
    }
    case "End": {
      event.preventDefault();
      navigateTo(year, 12);
      break;
    }
    case "Enter":
    case " ": {
      event.preventDefault();
      if (!isMonthDisabled(month, year)) {
        emit("jump", new Date(year, month - 1, 1));
      }
      break;
    }
  }
};

// Sync cursor to selection
watch(
  () => props.initialCursor,
  (date) => {
    cursor.value = createCursor(date.getFullYear(), date.getMonth() + 1);
  }
);

const focusSelectedMonth = (): void => {
  nextTick(() => {
    scrollSelectedYearIntoView();
    focusMonth(cursor.value.month);
  });
};

defineExpose({ focusSelectedMonth });
</script>

<style module>
.root {
  width: 272px;
  height: calc(264px + 38px);
}
</style>
